import { type SyncRun, howItWent } from '../../api/connections'
import { Button } from '../../ui/Control'

/**
 * When MyLib last looked into a foreign system, what it found there, and the control that looks again.
 *
 * It stands **under** the folders and not over them, because a sync is what those ticks do: the list
 * above answers "what comes over from here", and this answers the question somebody asks about it a
 * semester later — "when did it last come over, and what arrived". A control that started a sync above
 * the choice would be a control for a choice nobody has made yet.
 *
 * **The three numbers are sentences.** How many files were new, how many arrived as a new version and
 * how many are no longer over there describe a sync of documents completely, and they are what somebody
 * came here to read — so they are read out as sentences rather than set as a row of labels with figures
 * beside them. A count of zero is said too, for a run that finished: "finished, nothing new" is an
 * answer, and a missing line reads as something that did not work.
 *
 * **Nothing here ever says a sync deleted anything, because it never does.** A file that has gone from
 * the other system leaves a document that is still here, still openable and still findable, and says so
 * — that is the whole wording, and there is no second one anywhere on this screen.
 *
 * And **a run that ended in a refused password is a connection asking to be corrected**, not an error
 * message: it points at the password the same way the form's refusals point at a field, and it carries
 * the control that opens the form at that field.
 */
export function Sync({
  kind,
  brings,
  address,
  run,
  usable,
  busy,
  onSyncNow,
  onEnterPasswordAgain,
}: {
  /** What that system is called, as it calls itself. Said where something has gone from it. */
  readonly kind: string
  /**
   * What this system brings, which decides the **words** and nothing else.
   *
   * The three numbers a run answers with are the same either way — new, changed, no longer offered —
   * because they describe what happened rather than what was fetched. Whether they are read out as
   * files or as lessons is a question for a screen, and this is the answer to it.
   */
  readonly brings: 'FILES' | 'TIMETABLE'
  readonly address: string
  /** The last run, or `null` while there has never been one. */
  readonly run: SyncRun | null
  /**
   * Whether the password kept for this connection can still be read.
   *
   * A sync signs in over there, so a connection whose password cannot be opened cannot have one —
   * the control stands where it always does and says that by standing down, and the line at the top
   * of the connection says what to do about it.
   */
  readonly usable: boolean
  /** Whether something else is already on its way for this connection. */
  readonly busy: boolean
  readonly onSyncNow: () => void
  /** Opens the form for this connection, which is where a password is typed. */
  readonly onEnterPasswordAgain: () => void
}) {
  const running = run !== null && run.running

  return (
    <section className="sync" aria-label={`Syncing from ${address}`}>
      <h4 className="sync__title">Syncing</h4>

      {/*
        Never a run: an offer rather than a report of nothing. What is missing is a tick, so the
        sentence says where the ticks are and what happens once there is one.
      */}
      {run === null && (
        <p className="sync__none">
          {brings === 'TIMETABLE'
            ? 'Nothing has come over from here yet. Choose whose plan and which period above, and fetch it — afterwards MyLib looks again by itself every few hours, including while you are away.'
            : 'Nothing has come over from here yet. Tick the folders you want above and fetch them — afterwards MyLib looks again by itself every few hours, including while you are away.'}
        </p>
      )}

      {running && (
        <p className="sync__went" role="status">
          <span className="sync__outcome">Syncing now</span>
          <span className="sync__when">started {asMoment(run.startedAt)}</span>
          <span className="sync__aside">
            It runs in MyLib rather than in this page: leaving here does not stop it, and what
            arrives lands in your library as it comes.
          </span>
        </p>
      )}

      {run !== null && !running && (
        <Report
          run={run}
          kind={kind}
          brings={brings}
          address={address}
          onEnterPasswordAgain={onEnterPasswordAgain}
        />
      )}

      {/*
        Gone while a run is going on, rather than standing there greyed out. A control that cannot be
        used because the work is already under way is not an unavailable control — it is one that has
        just been used, and leaving it visible invites a second press for the same job. The line above
        it has taken its place and says what is happening.
      */}
      {!running && (
        <div className="controls controls--tight">
          <Button
            tone="quiet"
            aria-label={`Sync ${address} now`}
            disabled={!usable || busy}
            onClick={onSyncNow}
          >
            Sync now
          </Button>
        </div>
      )}
    </section>
  )
}

/**
 * One run that has ended: how it went, and what it did.
 *
 * The counts follow a finished run whatever they are, and a run that ended some other way only where
 * there is something to say. A refused sync that reported "nothing was new" would be making a claim
 * about the other system that it never got to look at — the numbers of a run that stopped describe
 * what it managed, not what is over there.
 */
function Report({
  run,
  kind,
  brings,
  address,
  onEnterPasswordAgain,
}: {
  readonly run: SyncRun
  readonly kind: string
  readonly brings: 'FILES' | 'TIMETABLE'
  readonly address: string
  readonly onEnterPasswordAgain: () => void
}) {
  const finished = run.outcome === 'FINISHED'
  const said = whatItDid(run, kind, brings, finished)

  return (
    <>
      <p className="sync__went">
        <span className="sync__outcome">{howItWent(run)}</span>
        <span className="sync__when">{asMoment(run.finishedAt ?? run.startedAt)}</span>
      </p>

      {run.outcome === 'ACCESS_REFUSED' && (
        <>
          <p className="sync__refused">
            {kind} did not accept the password kept here, so nothing was read. Nothing has been
            marked as missing because of that — a system that will not let MyLib in says nothing
            about what is inside it. Type the password in again and the next sync goes through.
          </p>
          <div className="controls controls--tight">
            <Button
              tone="quiet"
              aria-label={`Enter the password for ${address} again`}
              onClick={onEnterPasswordAgain}
            >
              Enter the password again
            </Button>
          </div>
        </>
      )}

      {run.outcome === 'NOT_REACHABLE' && run.problem === '' && (
        <p className="sync__aside">
          {kind} did not answer, or what answered was not {kind}. Nothing has been marked as missing
          because of it, and nothing else about this connection has changed. It is worth trying
          again in a while; if it keeps happening, the address is the value to look at.
        </p>
      )}

      {/*
        What the other side actually said, where it said anything.

        The reason this is here at all: "it did not answer" covers a server that is down and a
        request the other side refused for a reason somebody can act on, and reading the second as
        the first sends people to look at an address that was never wrong. It is shown in the
        system's own words rather than translated, because a sentence MyLib invented for a refusal
        it does not know is a sentence that can be wrong.
      */}
      {run.problem !== '' && (
        <p className="sync__refused">
          {kind} refused the request: <span className="sync__said">{run.problem}</span>
        </p>
      )}

      {run.outcome === null && (
        <p className="sync__aside">
          That run ended without saying how it went — most likely MyLib was restarted while it was
          going. Nothing is wrong with the connection, and nothing was left half done in your
          library: start another sync whenever you like.
        </p>
      )}

      {said.length > 0 && (
        <div className="sync__did">
          {!finished && (
            <p className="sync__sentence">What it had already done before it stopped:</p>
          )}
          {said.map((sentence) => (
            <p className="sync__sentence" key={sentence}>
              {sentence}
            </p>
          ))}
        </div>
      )}
    </>
  )
}

/**
 * What one run did, in the order somebody reads it: what came, what changed, what has gone.
 *
 * `everything` says the zeros as well, which only a finished run may do. Files that were passed over
 * are never said as a zero at all — nothing being too large is the ordinary case, and a line
 * announcing it every time would be a line about nothing.
 */
function whatItDid(
  run: SyncRun,
  kind: string,
  brings: 'FILES' | 'TIMETABLE',
  everything: boolean,
): string[] {
  if (brings === 'TIMETABLE') {
    const lines = [
      { how: run.added, said: whatArrivedInThePlan(run) },
      { how: run.newVersions, said: whatMoved(run) },
      { how: run.goneFromSource, said: whatIsNoLongerOffered(run, kind) },
    ]
    return lines.filter((line) => everything || line.how > 0).map((line) => line.said)
  }

  const lines = [
    { how: run.added, said: whatArrived(run) },
    { how: run.newVersions, said: whatChanged(run) },
    { how: run.goneFromSource, said: whatWentAway(run, kind) },
  ]
  return [
    ...lines.filter((line) => everything || line.how > 0).map((line) => line.said),
    ...(run.skipped === 0 ? [] : [whatWasPassedOver(run)]),
  ]
}

function whatArrived(run: SyncRun): string {
  if (run.added === 0) {
    return 'Nothing was new.'
  }
  return run.added === 1 ? 'One file was new.' : `${String(run.added)} files were new.`
}

/** Said with what happened to the earlier file, because that is the half somebody worries about. */
function whatChanged(run: SyncRun): string {
  if (run.newVersions === 0) {
    return 'Nothing had changed.'
  }
  return run.newVersions === 1
    ? 'One file had changed and came in as a new version — the earlier one is still there.'
    : `${String(run.newVersions)} files had changed and came in as new versions — the earlier ones are still there.`
}

/**
 * What has disappeared over there, and what that does here: nothing.
 *
 * "No longer in <that system>" is the wording, and it is the only one. A sync marks a document and
 * never removes one, so there is no sentence here that could be read as a deletion.
 */
function whatWentAway(run: SyncRun, kind: string): string {
  if (run.goneFromSource === 0) {
    return `Nothing has disappeared from ${kind}.`
  }
  return run.goneFromSource === 1
    ? `One document is no longer in ${kind}. It stays in your library and says so.`
    : `${String(run.goneFromSource)} documents are no longer in ${kind}. They stay in your library and say so.`
}

/**
 * The same three numbers, read out as a timetable.
 *
 * A separate set of sentences and not a word swapped into the ones above, because they are not the
 * same sentences with another noun in them: a file that changed becomes a version beside the old one,
 * and a lesson that changed *moved* — and what somebody wants to know about the second is that the
 * notes at it moved with it.
 */
function whatArrivedInThePlan(run: SyncRun): string {
  if (run.added === 0) {
    return 'No appointment was new.'
  }
  return run.added === 1
    ? 'One appointment was new.'
    : `${String(run.added)} appointments were new.`
}

function whatMoved(run: SyncRun): string {
  if (run.newVersions === 0) {
    return 'Nothing had changed.'
  }
  return run.newVersions === 1
    ? 'One appointment changed — it kept its place, so your notes and everything linked to it moved with it.'
    : `${String(run.newVersions)} appointments changed — they kept their places, so your notes and everything linked to them moved with them.`
}

function whatIsNoLongerOffered(run: SyncRun, kind: string): string {
  if (run.goneFromSource === 0) {
    return `Nothing has disappeared from ${kind}.`
  }
  return run.goneFromSource === 1
    ? `One appointment is no longer in ${kind}. It stays in your timetable and says so.`
    : `${String(run.goneFromSource)} appointments are no longer in ${kind}. They stay in your timetable and say so.`
}

/** What the run could not take. Said as something to know, not as something that failed. */
function whatWasPassedOver(run: SyncRun): string {
  return run.skipped === 1
    ? 'One file was passed over — too large for this MyLib, or unreadable.'
    : `${String(run.skipped)} files were passed over — too large for this MyLib, or unreadable.`
}

/**
 * A moment somebody can read, with the time of day in it.
 *
 * A sync is unlike a document: what matters is not which day it happened but how long ago, and
 * "today, 09:14" answers that where "9 Aug 2026" does not.
 */
function asMoment(iso: string): string {
  const at = new Date(iso)
  return Number.isNaN(at.getTime())
    ? ''
    : at.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}
